import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { companiesAction, globalAction } from 'common/actions';
import { COMPANY_LIST_BY_FIRM_QUERY_KEY } from 'common/constants/services/firms';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import { useResponse } from 'services/hooks/useResponse';
import { getCompanyListByFirm } from '../useGetCompanyListByFirm';
import { RefreshFirmCompaniesParams, UseRefreshFirmCompaniesReturn } from './useRefreshFirmCompanies.types';

const useRefreshFirmCompanies = (): UseRefreshFirmCompaniesReturn => {
  const [{ firmId: currentFirmId }, dispatch] = useStore() as UseStoreValues;
  const queryClient = useQueryClient();
  const { processErrorResponse } = useResponse();

  const refreshFirmCompanies = useCallback(
    async (params: RefreshFirmCompaniesParams = {}) => {
      const { firmId = currentFirmId, shouldShowLoadingProgress = false } = params;

      if (!firmId) {
        return [];
      }

      if (shouldShowLoadingProgress) {
        dispatch(globalAction.showLoadingProgress(true));
      }

      try {
        await queryClient.invalidateQueries({
          queryKey: [COMPANY_LIST_BY_FIRM_QUERY_KEY, firmId],
        });

        const companyList = await queryClient.fetchQuery({
          queryFn: () => getCompanyListByFirm({ firmId }),
          queryKey: [COMPANY_LIST_BY_FIRM_QUERY_KEY, firmId],
        });

        const companies = (companyList ?? []) as FirmCompany[];
        dispatch(companiesAction.setCompanyList(companies));
        return companies;
      } catch (error) {
        dispatch(companiesAction.setCompanyList([]));
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while loading Companies',
          action: 'get companies',
        });
        return [];
      } finally {
        if (shouldShowLoadingProgress) {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    [currentFirmId, dispatch, processErrorResponse, queryClient]
  );

  return {
    refreshFirmCompanies,
  };
};

export default useRefreshFirmCompanies;
